// Display · TextValue
//
// Closed-enum text primitive. Orthogonal axes — pick each independently.
// Every visual value comes from `@ds/tokens/palette`. No `style` escape.
//
// Axes:
//   size       — fontSize scale (xs..hero) — Bootstrap-shape
//   weight     — regular / medium / semibold / bold
//   tone       — shared Tone enum with Box
//   italic / uppercase / tabular / mono / truncate — boolean modifiers
//   align      — text-align
//   lineHeight — line-height scale

import type { MouseEvent, ReactNode } from "react";
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  LETTER_SPACING,
  LINE_HEIGHT,
} from "@ds/tokens/palette";
import { resolveTextTone, type Tone } from "@ds/tokens/tones";

// ─── Axes ─────────────────────────────────────────────────────────────────

export type TextSize = "micro" | "caption" | "xs" | "sm" | "md" | "lg" | "xl" | "hero";
export type TextWeight = "regular" | "medium" | "semibold" | "bold";
export type TextTone = Tone;

export type TextLineHeight = keyof typeof LINE_HEIGHT;
export type TextAlign = "left" | "center" | "right";

export type TextElement =
  | "span" | "div" | "p" | "label"
  | "h1" | "h2" | "h3" | "h4"
  | "strong" | "em" | "a";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface TextValueInput {
  children?: ReactNode;
  /** Value-with-fallback shorthand: empty values emit `fallback`. */
  value?: string | number | null | undefined;
  /** Placeholder when `value` is empty. Default `"—"`. */
  fallback?: string;

  /** Font size. Default `"sm"` (body 13). */
  size?: TextSize;
  /** Font weight. Default `"regular"`. */
  weight?: TextWeight;
  /** Color tone — shared enum with Box. */
  tone?: TextTone;

  /** Italic. */
  italic?: boolean;
  /** Uppercase + eyebrow letter-spacing. */
  uppercase?: boolean;
  /** Tabular numerals. */
  tabular?: boolean;
  /** Monospace font. */
  mono?: boolean;
  /**
   * Single-line truncation with ellipsis. `"fill"` truncates at the container's
   * width without the text sizing the container — for a line inside an auto-layout
   * table cell, where a plain `truncate` line widens the column to fit itself.
   */
  truncate?: boolean | "fill";
  /**
   * Multi-line truncation with ellipsis after this many lines (`-webkit-line-clamp`).
   * For copy that should show a preview of N lines then cut off — a free-text note
   * inside a fixed-width block (a map pin popover, a dense card). Ignored when
   * `truncate` is set: single-line truncation wins because the two overflow
   * strategies conflict. Keep the full text reachable — either a `title` or a
   * real `Tooltip` wrapper (note: a `Tooltip` must wrap a DOM element, not this
   * component, which spreads no injected props). Clamped text with neither is
   * exactly what the `ellipsis-no-tooltip` detector in `e2e-visual/` flags.
   * Unlike `truncate`, `clamp` does NOT auto-fill `title` for you.
   */
  clamp?: number;
  /** Prevent line-wrapping (no ellipsis). */
  nowrap?: boolean;
  /**
   * Honour the source's own newlines (`white-space: pre-line`). For text a
   * third party laid out — an invoice footer, a pasted note — where reflowing
   * to one paragraph loses the shape they printed. Collapses runs of spaces,
   * so it is safe for text that was column-padded.
   */
  preserveLines?: boolean;
  /** Wrap at word boundaries; a word only breaks if it cannot fit a line on its own. */
  wrapWords?: boolean;
  /** Unbreakable strings (IBANs, hashes) may break at any character; collapses min-content sizing. */
  wrapAnywhere?: boolean;

  /** Line-through decoration — for deleted line items, diff views. */
  strikethrough?: boolean;
  /**
   * Underline decoration style.
   * `"dotted"` — canonical clickable-label affordance (e.g. TransactionChecklist labels).
   * `"solid"`  — links or emphasis requiring a crisp underline.
   */
  underline?: "dotted" | "solid";
  /** Pass-through to the rendered element's HTML `title` attribute. Native browser tooltip for truncated text. */
  title?: string;

  /** Text alignment. */
  align?: TextAlign;
  /** Line-height token. */
  lineHeight?: TextLineHeight;

  /** Element type. Default `"span"`. */
  as?: TextElement;
  /** `htmlFor` (only meaningful when `as="label"`). */
  htmlFor?: string;
  /** `href` (only meaningful when `as="a"`). Automatically suppresses browser default text-decoration. */
  href?: string;
  /** Click handler — pointer cursor + non-selectable. */
  onClick?: (e: MouseEvent) => void;
  testId?: string;
  className?: string;

  // ── Back-compat aliases ────────────────────────────────────────────────
  /** @deprecated Use `tone="muted"`. */
  muted?: boolean;
  /** @deprecated Use `weight="medium"`. */
  strong?: boolean;
}

export type TextValueProps = TextValueInput;

// ─── Size mapping ─────────────────────────────────────────────────────────

const SIZE_TO_FONT: Record<TextSize, number> = {
  micro:   FONT_SIZE.micro,       // 10
  caption: FONT_SIZE.small,       // 12 — smallest meta text rendered as caption
  xs:      FONT_SIZE.label,       // 11
  sm:      FONT_SIZE.body,        // 13 — default body
  md:      FONT_SIZE.normal,      // 14
  lg:      FONT_SIZE.subheading,  // 16
  xl:      FONT_SIZE.h1,          // 28
  hero:    FONT_SIZE.hero,        // 36
};

// ─── Component ────────────────────────────────────────────────────────────

export function TextValue({
  children,
  value,
  fallback = "—",
  size,
  weight,
  tone,
  italic,
  uppercase,
  tabular,
  mono,
  truncate,
  clamp,
  nowrap,
  preserveLines,
  wrapWords,
  wrapAnywhere,
  strikethrough,
  underline,
  title,
  align,
  lineHeight,
  as = "span",
  htmlFor,
  href,
  onClick,
  testId,
  className,
  muted,
  strong,
}: TextValueProps) {
  const content: ReactNode =
    children !== undefined
      ? children
      : value == null || value === ""
        ? fallback
        : value;

  const effectiveTone:   TextTone   | undefined = tone   ?? (muted  ? "muted"  : undefined);
  const effectiveWeight: TextWeight | undefined = weight ?? (strong ? "medium" : undefined);

  const style: Record<string, unknown> = {};

  if (size)              style.fontSize   = SIZE_TO_FONT[size];
  if (preserveLines)     style.whiteSpace = "pre-line";
  if (effectiveWeight)   style.fontWeight = FONT_WEIGHT[effectiveWeight];
  if (effectiveTone) {
    const c = resolveTextTone(effectiveTone);
    if (c !== undefined) style.color = c;
  }
  if (align)      style.textAlign  = align;
  if (lineHeight) style.lineHeight = LINE_HEIGHT[lineHeight];

  if (italic)    style.fontStyle          = "italic";
  if (uppercase) { style.textTransform    = "uppercase"; style.letterSpacing = LETTER_SPACING.eyebrow; }
  if (tabular)   style.fontVariantNumeric = "tabular-nums";
  if (mono)      style.fontFamily         = FONT_FAMILY.mono;

  if (truncate) {
    style.overflow     = "hidden";
    style.textOverflow = "ellipsis";
    style.whiteSpace   = "nowrap";
    if (truncate === "fill") {
      style.display  = "block";
      style.width    = 0;
      style.minWidth = "100%";
    }
  } else if (clamp) {
    style.display          = "-webkit-box";
    style.WebkitBoxOrient  = "vertical";
    style.WebkitLineClamp  = clamp;
    style.overflow         = "hidden";
    style.overflowWrap     = "break-word";
    style.minWidth         = 0;
  } else if (nowrap) {
    style.whiteSpace = "nowrap";
  } else if (wrapAnywhere) {
    style.overflowWrap = "anywhere";
    style.wordBreak    = "break-word";
    style.minWidth     = 0;
  } else if (wrapWords) {
    style.overflowWrap = "break-word";
    style.minWidth     = 0;
  }

  // textDecoration: combine underline + strikethrough independently.
  // When both are set: "underline line-through" (space-separated shorthand).
  if (underline || strikethrough) {
    const parts: string[] = [];
    if (underline)     parts.push("underline");
    if (strikethrough) parts.push("line-through");
    style.textDecoration = parts.join(" ");
    if (underline) style.textDecorationStyle = underline;
  }

  if (onClick) {
    style.cursor     = "pointer";
    style.userSelect = "none";
  }

  // When rendered as an anchor, suppress the browser's default blue underline
  // so the primitive's own tone/underline props control decoration exclusively.
  if (as === "a") style.textDecoration = style.textDecoration ?? "none";

  const Tag = as;
  const extraProps: Record<string, unknown> = {};
  if (as === "label" && htmlFor !== undefined) extraProps.htmlFor = htmlFor;
  if (as === "a"     && href    !== undefined) extraProps.href    = href;
  if (title !== undefined) {
    extraProps.title = title;
  } else if (truncate && (typeof content === "string" || typeof content === "number")) {
    extraProps.title = String(content);
  }

  return (
    <Tag
      data-testid={testId}
      className={className}
      style={style}
      onClick={onClick}
      {...extraProps}
    >
      {content}
    </Tag>
  );
}
